/* eslint-disable */
import { useRef, useState, useEffect } from 'react';
import { Formik } from 'formik';
import { useMutation, useQuery } from 'react-fetching-library';
import { makeStyles } from '@material-ui/core';
import ContentWrapper from 'components/Wrappers/ContentWrapper';
import RoundedButton from 'components/Shared/RoundedButton';
import UnsavedChangesGuard from 'components/Shared/UnsavedChangesGuard';
import showServerError from 'helpers/showError';
import { ALL_ROUTES } from 'config/routes';
import { EMPTY_ADDRESS } from 'components/Shared/constants';
import useMetadataUpdate from 'hooks/useMetadataUpdate';
import BackButton from 'components/Shared/BackButton';
import { useDispatch, useSelector } from 'react-redux';
import {
  enqueueSnackbar,
  SNACKBAR_VARIANTS,
  getTransitionWithVariable,
  navigateToRoute,
  setAccountBackendErrors,
  clearAccountBackendErrors,
} from '../../../redux_store';
import AccountForm from './AccountForm/AccountForm';
import { AccountSchema, formattedAccountsValues } from '../helpers';

const useStyles = makeStyles((theme) => ({
  contentContainer: {
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
    position: 'relative',
    width: '100%',
  },
  actionsContainer: {
    position: 'absolute',
    bottom: 0,
    left: 0,
    right: 0,
    width: '100%',
    padding: theme.spacing(3, 6),
    background: '#ffffff',
    borderTop: '1px solid rgba(0, 0, 0, 0.12)',
    display: 'flex',
    justifyContent: 'flex-end',
    alignItems: 'center',
    gap: theme.spacing(2),
    boxShadow: '0 -4px 16px rgba(0, 0, 0, 0.08)',
    [theme.breakpoints.down('md')]: {
      padding: theme.spacing(2, 4),
    },
    [theme.breakpoints.down('sm')]: {
      padding: theme.spacing(2, 2),
      gap: theme.spacing(1),
    },
  },
  actionButton: {
    textTransform: 'uppercase',
    borderRadius: 8,
    padding: theme.spacing(1.5, 2),
    letterSpacing: 1.5,
    fontWeight: 600,
    fontSize: 14,
    minWidth: 100,
    height: 40,
    [theme.breakpoints.down('sm')]: {
      minWidth: 100,
      padding: theme.spacing(1, 2),
      fontSize: 13,
    },
  },
  backButton: {
    marginRight: 'auto',
  },
  section: {
    marginBottom: theme.spacing(3),
    padding: theme.spacing(4),
    background: '#ffffff',
    borderRadius: 12,
    border: '1px solid rgba(0, 0, 0, 0.08)',
    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.04)',
    [theme.breakpoints.down('md')]: {
      marginBottom: theme.spacing(2.5),
      padding: theme.spacing(3),
    },
    [theme.breakpoints.down('sm')]: {
      marginBottom: theme.spacing(2),
      padding: theme.spacing(2),
      borderRadius: 8,
    },
  },
  sectionTitle: {
    fontSize: '1.1rem',
    fontWeight: 600,
    color: theme.colors.blue2,
    marginBottom: theme.spacing(1.5),
    paddingBottom: theme.spacing(1),
    borderBottom: `2px solid ${theme.colors.lightBlue}`,
    [theme.breakpoints.down('sm')]: {
      fontSize: '1rem',
      marginBottom: theme.spacing(1),
    },
  },
}));

const AccountCreate = () => {
  const classes = useStyles();
  const dispatch = useDispatch();
  const { en: { btn, errors, notifications, titles, unsavedChanges } } = useSelector((state) => state.i18n);
  const { backendErrors } = useSelector((state) => state.accountEditing);
  const {
    user,
    isDistributionSponsor,
    metadata: { currentAccount },
  } = useSelector((state) => state.user);
  const [isSuccess, setIsSuccess] = useState(false);
  const [isFailed, setIsFailed] = useState(false);
  const [instantSurveysActive, setInstantSurveysActive] = useState(false);
  const formikRef = useRef();

  // Clear backend errors when component unmounts or user navigates away
  useEffect(() => {
    return () => {
      dispatch(clearAccountBackendErrors());
    };
  }, [dispatch]);

  // Fetch surveys and resources data once when page loads
  const { payload: surveysPayload, loading: surveysLoading } = useQuery({
    method: 'GET',
    endpoint: '/surveys',
  });

  const { payload: resourcesPayload, loading: resourcesLoading } = useQuery({
    method: 'GET',
    endpoint: '/resources',
  });

  const accountCreateAction = (values) => ({
    method: 'PUT',
    endpoint: '/accounts',
    body: values,
  });
  const { loading, mutate } = useMetadataUpdate(useMutation, [
    accountCreateAction,
  ]);

  const createAccount = async (values, { setSubmitting }) => {
    let formattedValues = formattedAccountsValues(values, true, {});
    dispatch(clearAccountBackendErrors());

    const { payload, error, status } = await mutate(formattedValues);
    if (error) {
      setIsFailed(true);
      setTimeout(() => {
        setIsFailed(false);
      }, 2000);
      const options = payload && {
        correlationUUID: payload.correlationUUID,
        userUUID: user.uuid,
      };
      setSubmitting(false);
      switch (status) {
        case 422: {
          showServerError(dispatch, errors.validationError);
          dispatch(setAccountBackendErrors(payload.validationErrors));
          break;
        }
        default: {
          showServerError(dispatch, '', options);
          break;
        }
      }
    } else if (payload) {
      setInstantSurveysActive(true);
      setIsSuccess(true);
      formikRef.current.resetForm({
        values: values,
        initialValues: values,
      });
      setTimeout(() => {
        dispatch(
          enqueueSnackbar({
            message: getTransitionWithVariable(notifications.accountCreated, {
              name: formattedValues.businessName,
            }),
            options: {
              variant: SNACKBAR_VARIANTS.success,
            },
          }),
        );
        // Navigate to user creation after successful account creation
        // Always redirect to Global Users Management with preselected account and auto-open create modal
        const queryParams = new URLSearchParams({
          preselectedAccountUuid: payload.uuid,
          preselectedAccountName: formattedValues.businessName,
          autoOpenCreateModal: 'true', // Signal to auto-open create user modal
        });
        const finalPath = ALL_ROUTES.user.globalManagement.path + '?' + queryParams.toString();

        dispatch(navigateToRoute({
          route: {
            path: finalPath,
            params: {
              currentAccountUuid: currentAccount.uuid,
            },
            title: titles.createUser,
          }
        }));
      }, 100);
      setTimeout(() => {
        setIsSuccess(false);
      }, 1000);
    }
  };

  return (
    <Formik
      innerRef={formikRef}
      initialValues={{
        isDistributionSponsor: false,
        accountPermissions: {
          disableAllergiendResources: false,
          disableOrdersAutomations: false,
          disableResourcesAll: false,
          disableAssessments: false,
          disableOrders: false,
          enableClaimsManagement: false,
          enabletImmunotherapyReport: false,
        },
        businessName: '',
        phone: '',
        fax: '',
        ianaTimeZone: 'America/New_York',
        contactName: '',
        isShippingTheSame: false,
        organizationAddress: {
          ...EMPTY_ADDRESS,
        },
        shippingAddress: {
          ...EMPTY_ADDRESS,
        },
        otherAddresses: [],
        scheduleDeliverTime: 540,
        allowedSurveys: [],
        allowedResources: [],
        instantSurveys: [],
        customTags: ['Other'],

        emailSurveyInvitationIsDefault: true,
        emailResourceInvitationIsDefault: true,
        emailSurveyReminderIsDefault: true,
        emailResourceReminderIsDefault: true,
        emailSurveyCompletionIsDefault: true,

        smsSurveyInvitationIsDefault: true,
        smsResourceInvitationIsDefault: true,
        smsSurveyReminderIsDefault: true,
        smsResourceReminderIsDefault: true,
        smsSurveyCompletionIsDefault: true,
        loading: 'Loading...',
      }}
      validateOnBlur={false}
      validateOnChange={false}
      validateOnMount={false}
      onSubmit={createAccount}
    >
      {({
          values,
          handleSubmit,
          dirty,
        }) => (
        <UnsavedChangesGuard
          hasUnsavedChanges={dirty}
          title={unsavedChanges.title}
          message={unsavedChanges.accountCreate}
          discardButtonText={unsavedChanges.leaveAnyway}
          keepEditingButtonText={unsavedChanges.keepEditing}
        >
          <ContentWrapper
            titleText={titles.createAccount}
            containerStyle={{
            overflow: 'visible',
            maxHeight: 'calc(100% - 35px)',
            boxSizing: 'border-box',
            paddingBottom: 0,
          }}
        >
          <div className={classes.contentContainer}>
            <AccountForm
              values={values}
              backendErrors={{ ...backendErrors }}
              accountUuid=''
              sectionClassName={classes.section}
              sectionTitleClassName={classes.sectionTitle}
              setBackendErrors={(errors) => dispatch(setAccountBackendErrors(errors))}
              surveysPayload={surveysPayload}
              resourcesPayload={resourcesPayload}
              surveysLoading={surveysLoading}
              resourcesLoading={resourcesLoading}
              instantSurveysActive={instantSurveysActive}
            />
            <div className={classes.actionsContainer}>
              <BackButton className={classes.backButton} />
              <RoundedButton
                variant='contained'
                color='primary'
                disabled={
                  loading ||
                  values.scheduleDeliverTime === 'invalid_value'
                }
                onClick={handleSubmit}
                isLoading={loading}
                isSuccess={isSuccess}
                isFailed={isFailed}
                className={classes.actionButton}
              >
                {btn.create}
              </RoundedButton>
            </div>
          </div>
        </ContentWrapper>
        </UnsavedChangesGuard>
      )}
    </Formik>
  );
};

export default AccountCreate;
